/**
 * Carrier Access — the carriers reaching a port, derived by the backend from
 * the related PortAllocation's description (`LM<CARRIER>` for last mile,
 * `CARR_<CARRIER>` for a backbone or trunk link).
 *
 * Shared by the ports list and the port detail card so both surfaces label and
 * order carriers identically. The backend already resolves the display `name`
 * (curated vendor, then the known-code map, then the raw code), so the UI
 * never maps codes itself.
 */

/**
 * Marker the backend read the carrier from, or `manual` for an Extended PoP
 * leg declared by hand (CTX-830).
 */
export type CarrierAccessSource = "lm" | "carr" | "manual";

/** Commercial type of a Carrier Access service (84-), as P3 sells it. */
export type CarrierAccessType = "dual" | "hybrid" | "satellite" | "single";

const ACCESS_TYPE_LABEL: Record<CarrierAccessType, string> = {
    dual: "Dual",
    hybrid: "Hybrid",
    satellite: "Satellite",
    single: "Single",
};

/**
 * Display label of the commercial type. Null is its own state — nobody has
 * captured the type yet — and must not read as "Single".
 */
export const accessTypeLabel = (type?: string | null): string =>
    ACCESS_TYPE_LABEL[type as CarrierAccessType] ?? "Not set";

/** Every commercial type, in label order, for a picker. */
export const ACCESS_TYPE_OPTIONS = Object.keys(
    ACCESS_TYPE_LABEL
) as CarrierAccessType[];

/** Medium of a carrier (CTX-800), captured in the Admin Center. */
export type CarrierMedium = "fiber" | "satellite";

export interface CarrierAccess {
    /** Carrier mnemonic as networking wrote it, e.g. `IENTC`. */
    carrierCode: string;
    /** Resolved display name; falls back to `carrierCode`. */
    name: string;
    source: CarrierAccessSource;
    /**
     * Extended PoP legs only: fiber or satellite, null while nobody captured
     * it. Ports never carry it.
     */
    medium?: CarrierMedium | null;
    /**
     * Extended PoP legs only: the sold Carrier Access (84-) the leg backs,
     * null while nobody declared it.
     */
    carrierAccess?: CarrierAccessRef | null;
}

/** The sold Carrier Access a leg backs, as the Extended PoP views serve it. */
export interface CarrierAccessRef {
    id: number;
    serviceId: string;
    name?: string | null;
    accessType: string | null;
}

const MEDIUM_LABEL: Record<CarrierMedium, string> = {
    fiber: "Fiber Optic",
    satellite: "Satellite",
};

/** Every medium, in label order, for a picker. */
export const CARRIER_MEDIUMS = Object.keys(MEDIUM_LABEL) as CarrierMedium[];

/** "Fiber Optic" / "Satellite"; "Not set" while nobody captured it. */
export const carrierMediumLabel = (medium?: string | null): string =>
    MEDIUM_LABEL[medium as CarrierMedium] ?? "Not set";
// The backend also stores the circuit and service request for each carrier but
// does not send them, since nothing displays them yet. See
// `transformCarrierAccess` in the backend's ports `common-helpers.js`.

/**
 * Read the `carrierAccess` array off a raw port payload.
 *
 * Ports served by a backend that predates the feature have no such field, so
 * an absent or malformed value is normalized to an empty array — callers can
 * then treat "no carrier" and "field not deployed yet" the same way.
 */
export const shapeCarrierAccess = (raw: unknown): CarrierAccess[] => {
    const entries = (raw as { carrierAccess?: unknown } | null | undefined)
        ?.carrierAccess;
    if (!Array.isArray(entries)) return [];

    return entries
        .filter(
            (entry): entry is CarrierAccess =>
                typeof entry?.carrierCode === "string" &&
                entry.carrierCode.trim() !== ""
        )
        .map((entry) => ({
            carrierCode: entry.carrierCode,
            name: entry.name?.trim() || entry.carrierCode,
            source: entry.source,
            ...(entry.medium !== undefined ? { medium: entry.medium } : {}),
            ...(entry.carrierAccess !== undefined
                ? { carrierAccess: entry.carrierAccess }
                : {}),
        }));
};

/**
 * Comma-separated carrier names for a read-only cell or detail row.
 *
 * Last mile is listed first: it is the carrier the customer's own access runs
 * over, so it is the one they came to the page for. Names are de-duplicated
 * because one carrier can be named by two different markers on the same port
 * (IENTC reaches port `10-C09582` as both its last mile and its trunk carrier),
 * and "IENTC, IENTC" would read as a bug.
 *
 * Returns an empty string when the port names no carrier, letting each surface
 * pick its own placeholder.
 */
export const formatCarrierAccessNames = (
    carriers: CarrierAccess[]
): string => {
    if (carriers.length === 0) return "";

    const lastMileFirst = [...carriers].sort((a, b) => {
        if (a.source === b.source) return a.name.localeCompare(b.name);
        return a.source === "lm" ? -1 : 1;
    });

    return [...new Set(lastMileFirst.map((carrier) => carrier.name))].join(
        ", "
    );
};

/**
 * One line per carrier with its medium (CTX-800): "Carrier A / Type: Fiber
 * Optic", as Javier asked, instead of every name on one line. The same carrier
 * on two CPEs of a site with the same medium reads once.
 */
export const carrierLines = (
    carriers: CarrierAccess[]
): { name: string; medium: string }[] => {
    const seen = new Map<string, { name: string; medium: string }>();
    for (const carrier of carriers) {
        const line = { name: carrier.name, medium: carrierMediumLabel(carrier.medium) };
        seen.set(`${line.name}|${line.medium}`, line);
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/** One sold Carrier Access of a site with its carriers, or the undeclared rest. */
export interface CarrierAccessGroup {
    /** Null for the carriers no Carrier Access claimed yet. */
    access: CarrierAccessRef | null;
    carriers: { name: string; medium: string }[];
}

/**
 * The site's carriers grouped by the Carrier Access (84-) they back: one
 * group per access, by id, then the carriers nobody declared yet. What the
 * Extended PoP list and detail show in their Carrier Access field.
 */
export const carrierAccessGroups = (
    carriers: CarrierAccess[]
): CarrierAccessGroup[] => {
    const byAccess = new Map<number, { access: CarrierAccessRef; carriers: CarrierAccess[] }>();
    const undeclared: CarrierAccess[] = [];
    for (const carrier of carriers) {
        const access = carrier.carrierAccess;
        if (!access) {
            undeclared.push(carrier);
            continue;
        }
        const group = byAccess.get(access.id) ?? { access, carriers: [] };
        group.carriers.push(carrier);
        byAccess.set(access.id, group);
    }

    const groups: CarrierAccessGroup[] = Array.from(byAccess.values())
        .sort((a, b) => a.access.serviceId.localeCompare(b.access.serviceId))
        .map((group) => ({ access: group.access, carriers: carrierLines(group.carriers) }));
    if (undeclared.length > 0) {
        groups.push({ access: null, carriers: carrierLines(undeclared) });
    }
    return groups;
};
